export const M9_UNIT_9 = {
  id: "m9-u9-first-conditional",
  title: "Unidad 9: Planning and Giving Advice",
  topic: "Master future planning! Learn the first conditional to give advice, make plans, and talk about real future possibilities. Essential for problem-solving and instructions!",
  iconType: "tools",
  grammarImage: "/grammar/grammar-m9-u9.jpg",
  grammarText: "FIRST CONDITIONAL: IF + present simple, WILL + verb. Use it for real future possibilities! 'If it rains, I'll stay home.' Perfect for plans, advice, and predictions!",
  
  sets: [
    // SET 1
    {
      id: "m9-u9-s1",
      title: "Set 1: First Conditional Basics - Real Possibilities 🎯",
      context: `**Planning the Weekend**

Sarah: "Hey Tom! What are your plans if the weather is nice this weekend?"
Tom: "If it's sunny, I'll go to the beach. What about you?"`,
      
      questions: [
        {
          type: "mc",
          q: "Which sentence talks about a REAL future possibility?",
          options: [
            "If it rains tomorrow, I'll stay home",
            "If I was rich, I would buy a yacht",
            "I stay home when it rains"
          ],
          a: "If it rains tomorrow, I'll stay home",
          tip: "First conditional = real future possibility!",
          successtip: "✓ REAL POSSIBILITY!\nFirst conditional is for things that MIGHT really happen in the future! 🌟"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si llueve, me quedaré en casa'",
          words: ["If", "it", "rains", "I'll", "stay", "home"],
          a: "If it rains I'll stay home",
          extra: ["will", "would"],
          tip: "IF + present simple, WILL + verb!",
          successtip: "✓ PERFECT PATTERN!\nIF + present (rains), WILL + verb (I'll stay)! This is the core of first conditional! 🎯"
        },
        {
          type: "mc",
          q: "What's the correct first conditional structure?",
          options: [
            "If I will see him, I'll tell him",
            "If I see him, I'll tell him",
            "If I saw him, I'll tell him"
          ],
          a: "If I see him, I'll tell him",
          tip: "After IF, use present simple (NOT will)!",
          successtip: "✓ CORRECT STRUCTURE!\nNEVER use 'will' after IF! Always: IF + present simple! 💪"
        },
        {
          type: "input",
          q: "Complete: 'If she _____ (call), I'll answer.'",
          a: "calls",
          tip: "Present simple after IF!",
          successtip: "✓ PRESENT SIMPLE!\nAfter IF, always use present simple, even for future meaning! 📞"
        },
        {
          type: "match",
          q: "Match the condition with the result:",
          matches: [
            { left: "If it snows,", right: "I'll go skiing" },
            { left: "If I'm hungry,", right: "I'll make a sandwich" },
            { left: "If they're late,", right: "we'll start without them" }
          ],
          successtip: "✓ CONDITION → RESULT!\nFirst part = condition (IF...), second part = result (WILL...)! ⚡"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si hace calor, iré a nadar'",
          words: ["If", "it's", "hot", "I'll", "go", "swimming"],
          a: "If it's hot I'll go swimming",
          extra: ["will", "went"],
          successtip: "✓ WEATHER PLANS!\nFirst conditional is PERFECT for making plans based on weather! ☀️"
        },
        {
          type: "mc",
          q: "Complete: 'If you study hard, you _____ the exam.'",
          options: [
            "pass",
            "will pass",
            "would pass"
          ],
          a: "will pass",
          tip: "Result uses WILL + verb!",
          successtip: "✓ WILL IN RESULT!\nThe result clause ALWAYS uses WILL (or won't) + base verb! 📚"
        },
        {
          type: "input",
          q: "Complete: 'If we _____ (not hurry), we'll be late.'",
          a: "don't hurry",
          alts: ["do not hurry"],
          tip: "Negative present simple after IF!",
          successtip: "✓ NEGATIVE CONDITION!\nFor negative conditions: IF + don't/doesn't + verb! ⏰"
        },
        {
          type: "traduce",
          q: "Traduce: '¿Vendrás si te invito?'",
          words: ["Will", "you", "come", "if", "I", "invite", "you"],
          a: "Will you come if I invite you",
          extra: ["came", "invited"],
          tip: "Questions can start with WILL!",
          successtip: "✓ CONDITIONAL QUESTION!\nYou can reverse the order: WILL you come IF I invite you? Same meaning! 🎉"
        },
        {
          type: "mc",
          q: "Which is correct?",
          options: [
            "If she will arrive early, we'll go",
            "If she arrives early, we'll go",
            "If she arrive early, we'll go"
          ],
          a: "If she arrives early, we'll go",
          successtip: "✓ THIRD PERSON!\nDon't forget -s/-es for he/she/it in the IF clause! (arrives, not arrive) 👤"
        }
      ]
    },

    // SET 2
    {
      id: "m9-u9-s2",
      title: "Set 2: DIY Tools Vocabulary 🔨",
      context: `**At the Hardware Store**

Customer: "I need to build a bookshelf. What tools do I need?"
Clerk: "You'll need a hammer, screwdriver, saw, and some nails and screws..."`,
      
      questions: [
        {
          type: "mc",
          q: "What tool do you use to drive nails into wood?",
          options: [
            "A hammer",
            "A screwdriver",
            "A saw"
          ],
          a: "A hammer",
          successtip: "✓ HAMMER!\nHammers have been used for thousands of years! Essential for any DIY project! 🔨"
        },
        {
          type: "match",
          q: "Match the tool with its use:",
          matches: [
            { left: "Screwdriver", right: "To turn screws" },
            { left: "Saw", right: "To cut wood" },
            { left: "Drill", right: "To make holes" }
          ],
          successtip: "✓ ESSENTIAL TOOLS!\nThese three tools are in almost every toolbox around the world! 🧰"
        },
        {
          type: "traduce",
          q: "Traduce: 'Necesito un destornillador y tornillos'",
          words: ["I", "need", "a", "screwdriver", "and", "screws"],
          a: "I need a screwdriver and screws",
          extra: ["screw", "hammer"],
          successtip: "✓ SCREWDRIVER & SCREWS!\nSCREWdriver turns SCREWS, just like the name suggests! 🔧"
        },
        {
          type: "mc",
          q: "What do you use to measure distances accurately?",
          options: [
            "A tape measure",
            "A level",
            "A vice"
          ],
          a: "A tape measure",
          tip: "It extends and has numbers on it!",
          successtip: "✓ TAPE MEASURE!\nIn the US: tape measure. In the UK: measuring tape. Same tool! 📏"
        },
        {
          type: "input",
          q: "Complete: 'To check if something is straight, use a _____.'",
          a: "level",
          alts: ["spirit level"],
          tip: "It has a bubble inside!",
          successtip: "✓ LEVEL!\nUS English: 'level'. UK English: 'spirit level'. The bubble shows if it's straight! 📐"
        },
        {
          type: "traduce",
          q: "Traduce: 'El taladro hace agujeros en la pared'",
          words: ["The", "drill", "makes", "holes", "in", "the", "wall"],
          a: "The drill makes holes in the wall",
          extra: ["hole", "drills"],
          successtip: "✓ DRILL!\nModern electric drills are one of the most useful power tools ever invented! ⚡"
        },
        {
          type: "match",
          q: "Match the fastener with its description:",
          matches: [
            { left: "Nail", right: "Hammered in" },
            { left: "Screw", right: "Turned in with a screwdriver" },
            { left: "Bolt", right: "Goes through with a nut" }
          ],
          tip: "Each has a different way to attach!",
          successtip: "✓ FASTENERS!\nNails = hammer. Screws = screwdriver. Bolts = nuts and wrench! 🔩"
        },
        {
          type: "mc",
          q: "What tool holds objects firmly in place while you work?",
          options: [
            "A vice",
            "A wrench",
            "Pliers"
          ],
          a: "A vice",
          tip: "It's fixed to a workbench!",
          successtip: "✓ VICE!\nA vice (US: vise) clamps objects so both your hands are free to work! 🗜️"
        },
        {
          type: "input",
          q: "Complete: 'To tighten or loosen a bolt, use a _____ or pliers.'",
          a: "wrench",
          alts: ["spanner"],
          tip: "US: wrench, UK: spanner!",
          successtip: "✓ WRENCH/SPANNER!\nSame tool, different names! US = wrench, UK = spanner! 🔧"
        },
        {
          type: "traduce",
          q: "Traduce: 'Las tenazas agarran cosas pequeñas'",
          words: ["Pliers", "grip", "small", "things"],
          a: "Pliers grip small things",
          extra: ["thing", "grips"],
          tip: "Pliers is always plural!",
          successtip: "✓ PLIERS!\nLike scissors or glasses, 'pliers' is always plural! One tool, plural noun! 🔧"
        }
      ]
    },

    // SET 3
    {
      id: "m9-u9-s3",
      title: "Set 3: Giving Advice with First Conditional 💡",
      context: `**Helping a Friend**

Mark: "My bookshelf is wobbling. What should I do?"
Anna: "If you tighten the screws, it'll be more stable. If that doesn't work, you'll need new brackets."`,
      
      questions: [
        {
          type: "mc",
          q: "Which gives the BEST advice using first conditional?",
          options: [
            "You should fix it",
            "If you tighten the screws, it'll be stable",
            "Tighten the screws"
          ],
          a: "If you tighten the screws, it'll be stable",
          tip: "First conditional shows cause and result!",
          successtip: "✓ CONDITIONAL ADVICE!\nFirst conditional makes advice more persuasive by showing the RESULT! 💪"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si estudias más, aprobarás'",
          words: ["If", "you", "study", "more", "you'll", "pass"],
          a: "If you study more you'll pass",
          extra: ["studied", "will"],
          successtip: "✓ MOTIVATIONAL ADVICE!\nThis pattern is perfect for encouraging people - show them the positive result! 📚"
        },
        {
          type: "input",
          q: "Complete the advice: 'If you _____ (practice) daily, you'll improve quickly.'",
          a: "practice",
          alts: ["practise"],
          tip: "Present simple after IF!",
          successtip: "✓ PRACTICE ADVICE!\nUS: practice (verb). UK: practise (verb). Both correct! 🎯"
        },
        {
          type: "match",
          q: "Match the problem with the advice:",
          matches: [
            { left: "I'm always tired", right: "If you sleep more, you'll feel better" },
            { left: "I can't find my keys", right: "If you check your coat, you'll find them" },
            { left: "My phone is slow", right: "If you delete apps, it'll run faster" }
          ],
          successtip: "✓ PROBLEM-SOLVING!\nFirst conditional is PERFECT for giving practical solutions! 🔧"
        },
        {
          type: "mc",
          q: "Complete: 'If you _____ healthy food, you'll lose weight.'",
          options: [
            "will eat",
            "eat",
            "ate"
          ],
          a: "eat",
          tip: "Present simple in the IF clause!",
          successtip: "✓ HEALTH ADVICE!\nFirst conditional is used ALL THE TIME for diet and fitness advice! 🥗"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si llegas temprano, conseguirás un buen asiento'",
          words: ["If", "you", "arrive", "early", "you'll", "get", "a", "good", "seat"],
          a: "If you arrive early you'll get a good seat",
          extra: ["will", "arrived"],
          successtip: "✓ PRACTICAL TIP!\nThis pattern is everywhere: advice columns, travel tips, life hacks! ✈️"
        },
        {
          type: "input",
          q: "Give advice: 'If you _____ (not save) money, you won't have enough.'",
          a: "don't save",
          alts: ["do not save"],
          tip: "Negative present simple!",
          successtip: "✓ WARNING ADVICE!\nNegative IF clauses are great for warnings: 'If you don't..., you won't...' ⚠️"
        },
        {
          type: "mc",
          q: "Which is the most natural advice?",
          options: [
            "If you'll be nice, people will like you",
            "If you're nice, people will like you",
            "If you are being nice, people will like you"
          ],
          a: "If you're nice, people will like you",
          successtip: "✓ NATURAL ENGLISH!\n'If you're nice' is much more natural than 'If you are being nice'! 🌟"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si no te apresuras, perderás el autobús'",
          words: ["If", "you", "don't", "hurry", "you'll", "miss", "the", "bus"],
          a: "If you don't hurry you'll miss the bus",
          extra: ["won't", "didn't"],
          successtip: "✓ URGENT ADVICE!\nThis pattern creates urgency: 'If you don't... you'll miss/lose...' ⏰"
        },
        {
          type: "match",
          q: "Match to create good advice:",
          matches: [
            { left: "If you listen carefully,", right: "you'll understand better" },
            { left: "If you ask questions,", right: "you'll learn faster" },
            { left: "If you take notes,", right: "you'll remember more" }
          ],
          tip: "All about learning!",
          successtip: "✓ STUDY TIPS!\nFirst conditional is the STANDARD pattern for all study advice! 📝"
        }
      ]
    },

    // SET 4
    {
      id: "m9-u9-s4",
      title: "Set 4: Making Plans & Predictions 🗓️",
      context: `**Weekend Plans**

Lisa: "What will you do this weekend?"
Jake: "If the weather is good, I'll go hiking. If it rains, I'll stay home and watch movies."`,
      
      questions: [
        {
          type: "mc",
          q: "Which shows a plan depending on a condition?",
          options: [
            "I'll go to the beach tomorrow",
            "If it's sunny, I'll go to the beach",
            "I go to the beach on Sundays"
          ],
          a: "If it's sunny, I'll go to the beach",
          successtip: "✓ CONDITIONAL PLAN!\nFirst conditional is PERFECT for plans that depend on something! ☀️"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si tengo tiempo, te visitaré'",
          words: ["If", "I", "have", "time", "I'll", "visit", "you"],
          a: "If I have time I'll visit you",
          extra: ["will", "had"],
          successtip: "✓ FLEXIBLE PLANS!\nWe use this ALL THE TIME when we're not 100% sure about our plans! 📅"
        },
        {
          type: "input",
          q: "Complete: 'If she _____ (finish) work early, she'll come to dinner.'",
          a: "finishes",
          tip: "Don't forget third-person -es!",
          successtip: "✓ THIRD PERSON!\nShe/he/it = finishes, arrives, comes (add -es after -sh, -ch, -s, -x, -z)! 👤"
        },
        {
          type: "match",
          q: "Match the condition with the plan:",
          matches: [
            { left: "If I save enough money,", right: "I'll buy a car" },
            { left: "If they invite me,", right: "I'll go to the party" },
            { left: "If we finish early,", right: "we'll grab dinner" }
          ],
          successtip: "✓ COMMON PLANS!\nThese are the most common types of conditional plans people make! 🎯"
        },
        {
          type: "mc",
          q: "Complete: 'If the train _____ on time, I'll make the meeting.'",
          options: [
            "will arrive",
            "arrives",
            "arrived"
          ],
          a: "arrives",
          tip: "Present simple after IF!",
          successtip: "✓ TRANSPORT PLANS!\nFirst conditional is super useful for travel plans with uncertainty! 🚂"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si me llama, le diré la verdad'",
          words: ["If", "he", "calls", "me", "I'll", "tell", "him", "the", "truth"],
          a: "If he calls me I'll tell him the truth",
          extra: ["will", "called"],
          successtip: "✓ FUTURE PROMISE!\nFirst conditional can express promises that depend on something happening! 📞"
        },
        {
          type: "input",
          q: "Complete: 'If it _____ (not rain), we'll have a picnic.'",
          a: "doesn't rain",
          alts: ["does not rain"],
          successtip: "✓ WEATHER PLANS!\n'If it doesn't rain' is one of the most common conditional phrases! 🌦️"
        },
        {
          type: "mc",
          q: "Which is a natural prediction?",
          options: [
            "If they work hard, they'll succeed",
            "If they will work hard, they succeed",
            "If they worked hard, they'll succeed"
          ],
          a: "If they work hard, they'll succeed",
          successtip: "✓ SUCCESS PREDICTION!\nFirst conditional is perfect for predicting likely future outcomes! 🌟"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si compro el coche, estaré sin dinero'",
          words: ["If", "I", "buy", "the", "car", "I'll", "be", "broke"],
          a: "If I buy the car I'll be broke",
          extra: ["will", "bought"],
          tip: "'broke' = sin dinero (informal)!",
          successtip: "✓ FINANCIAL PREDICTION!\n'Be broke' is super common informal English for 'have no money'! 💸"
        },
        {
          type: "match",
          q: "Complete these predictions:",
          matches: [
            { left: "If you eat too much,", right: "you'll feel sick" },
            { left: "If she doesn't study,", right: "she'll fail the exam" },
            { left: "If we leave now,", right: "we'll arrive on time" }
          ],
          successtip: "✓ LOGICAL PREDICTIONS!\nFirst conditional shows logical cause-and-effect in the future! ⚡"
        }
      ]
    },

    // SET 5
    {
      id: "m9-u9-s5",
      title: "Set 5: Instructions with First Conditional 📋",
      context: `**Assembly Instructions**

"IMPORTANT: If you don't follow these steps carefully, the furniture won't be stable. If you have questions, call our helpline."`,
      
      questions: [
        {
          type: "mc",
          q: "Which is the best instruction using first conditional?",
          options: [
            "Tighten all screws completely",
            "If you don't tighten the screws, it will wobble",
            "Tighten the screws or it wobbles"
          ],
          a: "If you don't tighten the screws, it will wobble",
          tip: "Shows the consequence clearly!",
          successtip: "✓ CLEAR INSTRUCTIONS!\nFirst conditional makes instructions clearer by showing WHY you should do it! 📝"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si no sigues las instrucciones, no funcionará'",
          words: ["If", "you", "don't", "follow", "the", "instructions", "it", "won't", "work"],
          a: "If you don't follow the instructions it won't work",
          extra: ["will", "didn't"],
          successtip: "✓ WARNING!\nThis pattern appears in EVERY instruction manual worldwide! ⚠️"
        },
        {
          type: "input",
          q: "Complete: 'If you _____ (use) the wrong tool, you'll damage it.'",
          a: "use",
          tip: "Present simple after IF!",
          successtip: "✓ SAFETY WARNING!\nFirst conditional is essential for safety instructions! 🔧"
        },
        {
          type: "match",
          q: "Match the instruction with the result:",
          matches: [
            { left: "If you measure twice,", right: "you'll cut once" },
            { left: "If you read the manual,", right: "you'll understand better" },
            { left: "If you check the level,", right: "it'll be straight" }
          ],
          tip: "Famous DIY wisdom!",
          successtip: "✓ DIY WISDOM!\n'Measure twice, cut once' is the golden rule of carpentry! 📏"
        },
        {
          type: "mc",
          q: "Complete the safety instruction: 'If you _____ safety goggles, you'll protect your eyes.'",
          options: [
            "will wear",
            "wear",
            "wore"
          ],
          a: "wear",
          successtip: "✓ SAFETY FIRST!\nSafety instructions ALWAYS use first conditional to show consequences! 🥽"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si aprietas demasiado, romperás el tornillo'",
          words: ["If", "you", "tighten", "too", "much", "you'll", "break", "the", "screw"],
          a: "If you tighten too much you'll break the screw",
          extra: ["will", "tightened"],
          successtip: "✓ PRECISION WARNING!\nThis type of warning prevents common mistakes in assembly! 🔩"
        },
        {
          type: "input",
          q: "Complete: 'If you _____ (not be) careful, you'll hurt yourself.'",
          a: "aren't",
          alts: ["are not", "re not"],
          tip: "BE in present simple = am/are/is!",
          successtip: "✓ GENERAL WARNING!\nThis is THE most common safety warning in any language! ⚠️"
        },
        {
          type: "mc",
          q: "Which instruction is clearest?",
          options: [
            "Check the battery",
            "If you don't check the battery, it won't work",
            "The battery needs checking"
          ],
          a: "If you don't check the battery, it won't work",
          tip: "Shows cause and effect!",
          successtip: "✓ TROUBLESHOOTING!\nFirst conditional is perfect for troubleshooting guides! 🔋"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si tienes dudas, llama al servicio técnico'",
          words: ["If", "you", "have", "questions", "call", "technical", "support"],
          a: "If you have questions call technical support",
          extra: ["will", "question"],
          tip: "This mixes conditional + imperative!",
          successtip: "✓ HELP INSTRUCTION!\nIF + present, imperative (no will) is also common in instructions! 📞"
        },
        {
          type: "match",
          q: "Match the tool instruction:",
          matches: [
            { left: "If you use a hammer,", right: "you'll drive nails easily" },
            { left: "If you use a drill,", right: "you'll make clean holes" },
            { left: "If you use a saw,", right: "you'll cut wood straight" }
          ],
          successtip: "✓ TOOL FUNCTIONS!\nFirst conditional describes what each tool will accomplish! 🛠️"
        }
      ]
    },

    // SET 6
    {
      id: "m9-u9-s6",
      title: "Set 6: UNLESS = If not 🚫",
      context: `**Important Notice**

"Unless you register by Friday, you won't get a place in the course. Unless we receive payment, your order won't be processed."`,
      
      questions: [
        {
          type: "mc",
          q: "Which two sentences mean the SAME thing?",
          options: [
            "Unless you hurry = If you hurry",
            "Unless you hurry = If you don't hurry",
            "Unless you hurry = When you hurry"
          ],
          a: "Unless you hurry = If you don't hurry",
          tip: "UNLESS = IF NOT!",
          successtip: "✓ UNLESS = IF NOT!\nThis is the KEY rule! UNLESS means IF NOT! 🔑"
        },
        {
          type: "traduce",
          q: "Traduce: 'A menos que estudies, no aprobarás'",
          words: ["Unless", "you", "study", "you", "won't", "pass"],
          a: "Unless you study you won't pass",
          extra: ["will", "don't"],
          tip: "UNLESS already means 'if not' - don't add don't!",
          successtip: "✓ UNLESS!\nDon't use DON'T after UNLESS! 'Unless you study' = 'If you DON'T study'! ⚠️"
        },
        {
          type: "input",
          q: "Rewrite with UNLESS: 'If you don't leave now, you'll be late.'",
          a: "Unless you leave now you'll be late",
          alts: ["Unless you leave now, you'll be late"],
          tip: "Remove 'don't' when you use UNLESS!",
          successtip: "✓ CONVERSION!\nIF you DON'T = UNLESS you DO (positive verb after unless)! 🔄"
        },
        {
          type: "match",
          q: "Match IF NOT with UNLESS:",
          matches: [
            { left: "If you don't hurry", right: "Unless you hurry" },
            { left: "If it doesn't rain", right: "Unless it rains" },
            { left: "If he doesn't call", right: "Unless he calls" }
          ],
          successtip: "✓ PERFECT MATCH!\nJust remove DON'T/DOESN'T and replace IF with UNLESS! Easy! 🎯"
        },
        {
          type: "mc",
          q: "Which is correct?",
          options: [
            "Unless you don't pay, we'll cancel",
            "Unless you pay, we'll cancel",
            "Unless you will pay, we'll cancel"
          ],
          a: "Unless you pay, we'll cancel",
          tip: "UNLESS + positive verb (no don't, no will)!",
          successtip: "✓ COMMON MISTAKE!\nNEVER put DON'T after UNLESS! It already means 'if not'! 🚫"
        },
        {
          type: "traduce",
          q: "Traduce: 'A menos que llueva, iremos al parque'",
          words: ["Unless", "it", "rains", "we'll", "go", "to", "the", "park"],
          a: "Unless it rains we'll go to the park",
          extra: ["will", "rain"],
          successtip: "✓ WEATHER PLANS!\nUNLESS is very common for weather-dependent plans! 🌧️"
        },
        {
          type: "input",
          q: "Complete: 'Unless she _____ (arrive) soon, we'll start without her.'",
          a: "arrives",
          tip: "Present simple after UNLESS (like after IF)!",
          successtip: "✓ GRAMMAR RULE!\nUNLESS follows the same grammar as IF: UNLESS + present simple! ⏰"
        },
        {
          type: "mc",
          q: "What does this mean? 'Unless you help me, I won't finish.'",
          options: [
            "If you help me, I won't finish",
            "If you don't help me, I won't finish",
            "When you help me, I won't finish"
          ],
          a: "If you don't help me, I won't finish",
          successtip: "✓ MEANING!\nUNLESS you help = IF you DON'T help. Always check the meaning! 💡"
        },
        {
          type: "traduce",
          q: "Traduce: 'A menos que tengas cuidado, te lastimarás'",
          words: ["Unless", "you're", "careful", "you'll", "hurt", "yourself"],
          a: "Unless you're careful you'll hurt yourself",
          extra: ["don't", "will"],
          successtip: "✓ WARNING WITH UNLESS!\nUNLESS makes warnings stronger and more urgent! ⚠️"
        },
        {
          type: "match",
          q: "Match to create warnings with UNLESS:",
          matches: [
            { left: "Unless you save money,", right: "you won't buy a house" },
            { left: "Unless we hurry,", right: "we'll miss the train" },
            { left: "Unless he studies,", right: "he'll fail the exam" }
          ],
          successtip: "✓ STRONG WARNINGS!\nUNLESS sounds more formal and urgent than IF NOT! Use it for important warnings! 🔔"
        }
      ]
    },

    // SET 7
    {
      id: "m9-u9-s7",
      title: "Set 7: Complex First Conditional Situations 🎓",
      context: `**Project Planning Meeting**

Manager: "If we finish the design by Monday, we'll send it to the client. If they approve it quickly, we'll start production next week. Unless there are delays, we'll meet the deadline."`,
      
      questions: [
        {
          type: "mc",
          q: "Which sentence chain shows a sequence of conditional events?",
          options: [
            "I'll go home and watch TV",
            "If I finish work, I'll go home. If I go home, I'll watch TV",
            "I finish work and go home"
          ],
          a: "If I finish work, I'll go home. If I go home, I'll watch TV",
          tip: "Multiple IFs show a chain of events!",
          successtip: "✓ CONDITIONAL CHAIN!\nYou can connect multiple first conditionals to show a sequence! 🔗"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si consigo el trabajo, me mudaré. Si me mudo, seré más feliz'",
          words: ["If", "I", "get", "the", "job", "I'll", "move", "If", "I", "move", "I'll", "be", "happier"],
          a: "If I get the job I'll move If I move I'll be happier",
          extra: ["will", "got"],
          successtip: "✓ LIFE DECISIONS!\nConditional chains help us think through major life decisions! 🌟"
        },
        {
          type: "input",
          q: "Complete: 'If we _____ (save) enough, we'll buy a house. If we _____ (buy) a house, we'll be happy.'",
          a: "save, buy",
          tip: "Both use present simple after IF!",
          successtip: "✓ DOUBLE CONDITIONAL!\nEach IF clause still uses present simple, even when there are multiple! 🏠"
        },
        {
          type: "match",
          q: "Put these events in logical order:",
          matches: [
            { left: "First:", right: "If I study hard, I'll pass" },
            { left: "Then:", right: "If I pass, I'll get a degree" },
            { left: "Finally:", right: "If I get a degree, I'll find a good job" }
          ],
          successtip: "✓ LONG-TERM PLANNING!\nConditional chains are perfect for planning multi-step goals! 🎯"
        },
        {
          type: "mc",
          q: "Which combines first conditional with UNLESS correctly?",
          options: [
            "If you help me, I'll finish. Unless you don't help, I'll finish",
            "If you help me, I'll finish. Unless you help me, I won't finish",
            "If you help me, I finish. Unless you help me, I don't finish"
          ],
          a: "If you help me, I'll finish. Unless you help me, I won't finish",
          tip: "Watch the positive/negative switch!",
          successtip: "✓ IF vs UNLESS!\nYou can express the same idea positively (IF) or negatively (UNLESS)! 🔄"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si hace sol, iremos a la playa, a menos que estés cansada'",
          words: ["If", "it's", "sunny", "we'll", "go", "to", "the", "beach", "unless", "you're", "tired"],
          a: "If it's sunny we'll go to the beach unless you're tired",
          extra: ["will", "don't"],
          successtip: "✓ COMPOUND CONDITIONAL!\nYou can combine IF and UNLESS in one sentence for complex conditions! 🏖️"
        },
        {
          type: "input",
          q: "Complete: 'If the price _____ (be) good and they _____ (deliver) quickly, I'll buy it.'",
          a: "is, deliver",
          tip: "Two conditions, both present simple!",
          successtip: "✓ MULTIPLE CONDITIONS!\nYou can have TWO+ conditions in the IF clause with AND/OR! 📦"
        },
        {
          type: "mc",
          q: "Which shows the most sophisticated use of first conditional?",
          options: [
            "If it rains, I'll stay home",
            "Unless we leave soon, we'll be late, and if we're late, the boss will be angry",
            "I'll go if you go"
          ],
          a: "Unless we leave soon, we'll be late, and if we're late, the boss will be angry",
          successtip: "✓ B2 LEVEL!\nCombining UNLESS, IF, and conditional chains = advanced English! 🌟"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si ella acepta el trabajo o si él renuncia, necesitaremos a alguien nuevo'",
          words: ["If", "she", "accepts", "the", "job", "or", "if", "he", "quits", "we'll", "need", "someone", "new"],
          a: "If she accepts the job or if he quits we'll need someone new",
          extra: ["will", "accepted"],
          tip: "Two possible conditions with OR!",
          successtip: "✓ ALTERNATIVE CONDITIONS!\nOR shows that EITHER condition leads to the same result! 💼"
        },
        {
          type: "match",
          q: "Match the business conditional:",
          matches: [
            { left: "If the client approves,", right: "we'll start production" },
            { left: "Unless costs decrease,", right: "we'll raise prices" },
            { left: "If sales improve and expenses drop,", right: "we'll make a profit" }
          ],
          successtip: "✓ BUSINESS ENGLISH!\nFirst conditional is ESSENTIAL in business for planning and forecasting! 📊"
        }
      ]
    },

    // SET 8
    {
      id: "m9-u9-s8",
      title: "Set 8: Real-World Applications & Mastery 🏆",
      context: `**Final Challenge**

You've mastered the first conditional! Now let's apply it to real situations: recipes, travel, technology, and everyday problem-solving.`,
      
      questions: [
        {
          type: "mc",
          q: "Which is a natural recipe instruction?",
          options: [
            "If you add salt, it tastes better",
            "If you'll add salt, it'll taste better",
            "If you add salt, it'll taste better"
          ],
          a: "If you add salt, it'll taste better",
          tip: "First conditional for results!",
          successtip: "✓ COOKING!\nFirst conditional is everywhere in recipes for optional ingredients! 👨‍🍳"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si reservas con anticipación, ahorrarás dinero'",
          words: ["If", "you", "book", "in", "advance", "you'll", "save", "money"],
          a: "If you book in advance you'll save money",
          extra: ["will", "booked"],
          successtip: "✓ TRAVEL TIP!\nThis exact sentence appears in MILLIONS of travel websites! ✈️"
        },
        {
          type: "input",
          q: "Complete: 'If you _____ (update) the software, your phone _____ (run) faster.'",
          a: "update, will run",
          alts: ["update, 'll run"],
          tip: "Tech advice pattern!",
          successtip: "✓ TECH SUPPORT!\nFirst conditional is the STANDARD pattern for all tech troubleshooting! 📱"
        },
        {
          type: "match",
          q: "Match the context with the conditional:",
          matches: [
            { left: "Health:", right: "If you exercise daily, you'll lose weight" },
            { left: "Finance:", right: "If you save 10%, you'll have money for emergencies" },
            { left: "Relationships:", right: "If you communicate well, you'll avoid conflicts" }
          ],
          successtip: "✓ UNIVERSAL PATTERN!\nFirst conditional works in EVERY area of life advice! 🌍"
        },
        {
          type: "mc",
          q: "Choose the most natural sentence:",
          options: [
            "If you will come to the party, I will be happy",
            "If you come to the party, I'll be happy",
            "If you came to the party, I'll be happy"
          ],
          a: "If you come to the party, I'll be happy",
          successtip: "✓ NATURAL ENGLISH!\nNative speakers ALWAYS use: IF + present, WILL + verb! 🎉"
        },
        {
          type: "traduce",
          q: "Traduce: 'A menos que canceles tu suscripción, te cobrarán el próximo mes'",
          words: ["Unless", "you", "cancel", "your", "subscription", "you'll", "be", "charged", "next", "month"],
          a: "Unless you cancel your subscription you'll be charged next month",
          extra: ["don't", "will"],
          successtip: "✓ SUBSCRIPTION WARNING!\nThis exact conditional appears in every online service! 💳"
        },
        {
          type: "input",
          q: "Complete: 'If there _____ (be) a problem or if you _____ (need) help, call us anytime.'",
          a: "is, need",
          alts: ["'s, need"],
          tip: "Customer service language!",
          successtip: "✓ CUSTOMER SERVICE!\nThis pattern is in every customer service message worldwide! 📞"
        },
        {
          type: "mc",
          q: "Which shows B2-level mastery?",
          options: [
            "If it rains, I'll use my umbrella",
            "If you invest wisely, diversify your portfolio, and monitor the market, you'll minimize risks",
            "I'll go if you go"
          ],
          a: "If you invest wisely, diversify your portfolio, and monitor the market, you'll minimize risks",
          tip: "Multiple conditions + sophisticated vocabulary!",
          successtip: "✓ B2 MASTERY!\nCombining multiple conditions with advanced vocabulary = B2 level! 📈"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si no estás satisfecho con tu compra, te devolveremos el dinero'",
          words: ["If", "you're", "not", "satisfied", "with", "your", "purchase", "we'll", "refund", "your", "money"],
          a: "If you're not satisfied with your purchase we'll refund your money",
          extra: ["don't", "will"],
          successtip: "✓ GUARANTEE!\nThis is THE standard money-back guarantee in business English! 💰"
        },
        {
          type: "match",
          q: "Match the professional context:",
          matches: [
            { left: "Email:", right: "If you need any further information, please let me know" },
            { left: "Meeting:", right: "Unless there are objections, we'll proceed with the plan" },
            { left: "Presentation:", right: "If we implement these changes, we'll see results within 6 months" }
          ],
          successtip: "✓ PROFESSIONAL ENGLISH!\n🎉 CONGRATULATIONS! You've mastered first conditional - essential for B2 level! You can now give advice, make plans, and discuss possibilities like a pro! 🌟"
      }
      ]
    },
    // SET 9
    {
      id: "m9-u9-s9",
      title: "Set 9: First Conditional in Conversations 💬",
      context: `**Casual Chat**

Emma: "Hey, are you free Saturday?"
Mike: "If I finish my project, I'll be free. If not, I'll have to work. Why?"
Emma: "If you're free, let's go hiking!"`,
      
      questions: [
        {
          type: "mc",
          q: "Which is the most natural response?",
          options: [
            "If you'll go, I'll go too",
            "If you go, I'll go too",
            "If you went, I'll go too"
          ],
          a: "If you go, I'll go too",
          tip: "Natural conversation = simple structure!",
          successtip: "✓ NATURAL SPEECH!\nIn real conversations, people use first conditional ALL THE TIME! 🗣️"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si tú pagas la cena, yo pagaré las bebidas'",
          words: ["If", "you", "pay", "for", "dinner", "I'll", "pay", "for", "drinks"],
          a: "If you pay for dinner I'll pay for drinks",
          extra: ["will", "paid"],
          successtip: "✓ MAKING DEALS!\nFirst conditional is perfect for making fair agreements! 🤝"
        },
        {
          type: "input",
          q: "Complete: 'If you _____ (tell) me the address, I _____ (meet) you there.'",
          a: "tell, 'll meet",
          alts: ["tell, will meet"],
          tip: "Coordination and planning!",
          successtip: "✓ COORDINATION!\nThis pattern is used constantly when making plans with friends! 📍"
        },
        {
          type: "match",
          q: "Match the offer with the response:",
          matches: [
            { left: "If you cook, I'll clean", right: "Deal!" },
            { left: "If you drive, I'll pay gas", right: "Sounds fair!" },
            { left: "If you help me move, I'll buy pizza", right: "Okay, I'm in!" }
          ],
          successtip: "✓ NEGOTIATIONS!\nFirst conditional makes offers and trades crystal clear! 💯"
        },
        {
          type: "mc",
          q: "Which shows enthusiasm in conversation?",
          options: [
            "If you come, it will be good",
            "If you come, it'll be amazing!",
            "If you will come, it's amazing"
          ],
          a: "If you come, it'll be amazing!",
          tip: "Exclamation + strong adjective!",
          successtip: "✓ ENTHUSIASM!\nAdd strong adjectives (amazing, awesome, fantastic) to show excitement! 🎉"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si me prestas tu bici, te daré la mía mañana'",
          words: ["If", "you", "lend", "me", "your", "bike", "I'll", "give", "you", "mine", "tomorrow"],
          a: "If you lend me your bike I'll give you mine tomorrow",
          extra: ["will", "borrow"],
          tip: "Lend = prestar (tú das), borrow = pedir prestado (tú recibes)!",
          successtip: "✓ LENDING!\nLEND vs BORROW: I lend TO you, you borrow FROM me! 🚲"
        },
        {
          type: "input",
          q: "Complete: 'If she _____ (say) yes, we _____ (celebrate) tonight!'",
          a: "says, 'll celebrate",
          alts: ["says, will celebrate"],
          successtip: "✓ CELEBRATING PLANS!\nFirst conditional + celebration vocabulary = excited planning! 🥳"
        },
        {
          type: "mc",
          q: "Which is the best way to suggest plans?",
          options: [
            "We should go to the movies",
            "If you want, we can go to the movies",
            "Let's go to the movies"
          ],
          a: "If you want, we can go to the movies",
          tip: "Gives the other person choice!",
          successtip: "✓ POLITE SUGGESTIONS!\n'If you want...' is very polite - it gives the other person freedom! 🎬"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si no te gusta, podemos hacer otra cosa'",
          words: ["If", "you", "don't", "like", "it", "we", "can", "do", "something", "else"],
          a: "If you don't like it we can do something else",
          extra: ["will", "won't"],
          tip: "CAN is also common in first conditional!",
          successtip: "✓ FLEXIBILITY!\nIF + present, CAN + verb is also first conditional - shows flexibility! 🔄"
        },
        {
          type: "match",
          q: "Match the invitation pattern:",
          matches: [
            { left: "If you're interested,", right: "we could check it out" },
            { left: "If you have time,", right: "we can grab coffee" },
            { left: "If you're not busy,", right: "we could hang out" }
          ],
          successtip: "✓ INVITATIONS!\nIF + you're/you have + suggestion = super natural invitation pattern! ☕"
        }
      ]
    },

    // SET 10
    {
      id: "m9-u9-s10",
      title: "Set 10: Problem-Solving with First Conditional 🔧",
      context: `**Tech Support Call**

"If your device isn't working, try these steps. If you restart it, that might fix the problem. If the problem continues, we'll send a technician."`,
      
      questions: [
        {
          type: "mc",
          q: "Which is the best troubleshooting advice?",
          options: [
            "Restart your computer",
            "If you restart your computer, it might work",
            "Your computer needs restarting"
          ],
          a: "If you restart your computer, it might work",
          tip: "Shows the potential solution!",
          successtip: "✓ TROUBLESHOOTING!\nFirst conditional is THE standard pattern for tech support! 💻"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si borras la aplicación y la reinstolas, funcionará mejor'",
          words: ["If", "you", "delete", "the", "app", "and", "reinstall", "it", "it'll", "work", "better"],
          a: "If you delete the app and reinstall it it'll work better",
          extra: ["will", "deleted"],
          successtip: "✓ APP TROUBLESHOOTING!\nThis is classic tech advice - delete and reinstall! 📱"
        },
        {
          type: "input",
          q: "Complete: 'If the problem _____ (persist), _____ (contact) customer service.'",
          a: "persists, contact",
          tip: "IF + present, imperative!",
          successtip: "✓ ESCALATION!\nIF + problem persists, imperative = standard customer service language! 📞"
        },
        {
          type: "match",
          q: "Match the problem with the solution:",
          matches: [
            { left: "If it won't turn on,", right: "check the battery" },
            { left: "If it's running slow,", right: "close some apps" },
            { left: "If it keeps crashing,", right: "update the software" }
          ],
          successtip: "✓ COMMON FIXES!\nThese are the top 3 tech fixes for 90% of problems! 🔋"
        },
        {
          type: "mc",
          q: "Which gives a complete troubleshooting sequence?",
          options: [
            "Restart it",
            "If restarting doesn't work, try resetting. If that fails, contact us",
            "Try restarting and resetting"
          ],
          a: "If restarting doesn't work, try resetting. If that fails, contact us",
          tip: "Multiple steps with conditionals!",
          successtip: "✓ STEP-BY-STEP!\nConditional sequences guide users through multiple solutions! 📋"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si el error continúa, desconecta el cable y reconéctalo'",
          words: ["If", "the", "error", "continues", "unplug", "the", "cable", "and", "plug", "it", "back", "in"],
          a: "If the error continues unplug the cable and plug it back in",
          extra: ["will", "continued"],
          tip: "Imperative in the result clause!",
          successtip: "✓ POWER CYCLE!\n'Unplug and plug back in' = universal tech fix #1! 🔌"
        },
        {
          type: "input",
          q: "Complete: 'If you _____ (experience) issues, _____ (clear) your browser cache.'",
          a: "experience, clear",
          alts: ["are experiencing, clear"],
          successtip: "✓ BROWSER FIX!\nClearing cache solves SO many problems - classic advice! 🌐"
        },
        {
          type: "mc",
          q: "Which uses UNLESS correctly for troubleshooting?",
          options: [
            "Unless you don't update, it won't work",
            "Unless you update, it won't work properly",
            "Unless you will update, it won't work"
          ],
          a: "Unless you update, it won't work properly",
          successtip: "✓ UNLESS WARNING!\nUNLESS adds urgency to tech warnings - do this OR ELSE! ⚠️"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si eso no soluciona el problema, necesitarás una reparación'",
          words: ["If", "that", "doesn't", "fix", "the", "problem", "you'll", "need", "a", "repair"],
          a: "If that doesn't fix the problem you'll need a repair",
          extra: ["will", "didn't"],
          successtip: "✓ LAST RESORT!\nThis pattern shows when DIY fixes won't work anymore! 🛠️"
        },
        {
          type: "match",
          q: "Match the condition type:",
          matches: [
            { left: "If the light is red,", right: "it needs charging" },
            { left: "If you hear a beep,", right: "it's an error code" },
            { left: "If the screen is blank,", right: "it's not receiving power" }
          ],
          successtip: "✓ DIAGNOSTIC PATTERNS!\nIF + symptom, explanation = how experts diagnose problems! 🔍"
        }
      ]
    },

    // SET 11
    {
      id: "m9-u9-s11",
      title: "Set 11: First Conditional in Professional Contexts 💼",
      context: `**Project Meeting**

"If we meet this deadline, we'll get the bonus. If anyone needs resources, let me know. Unless we work as a team, we won't succeed."`,
      
      questions: [
        {
          type: "mc",
          q: "Which is the most professional way to state a condition?",
          options: [
            "If you finish fast, it's good",
            "If we complete this on time, we'll exceed client expectations",
            "Finish on time and it's great"
          ],
          a: "If we complete this on time, we'll exceed client expectations",
          tip: "Formal vocabulary + specific result!",
          successtip: "✓ PROFESSIONAL TONE!\nFormal verbs (complete, exceed) + specific outcomes = B2 business English! 💼"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si implementamos estos cambios, veremos mejoras en el primer trimestre'",
          words: ["If", "we", "implement", "these", "changes", "we'll", "see", "improvements", "in", "Q1"],
          a: "If we implement these changes we'll see improvements in Q1",
          extra: ["will", "implemented"],
          tip: "Q1 = first quarter (trimestre)!",
          successtip: "✓ BUSINESS PROJECTIONS!\nFirst conditional is ESSENTIAL for business forecasting! 📊"
        },
        {
          type: "input",
          q: "Complete: 'If sales _____ (increase), we _____ (expand) to new markets.'",
          a: "increase, 'll expand",
          alts: ["increase, will expand"],
          successtip: "✓ GROWTH PLANNING!\nThis pattern appears in every business strategy document! 📈"
        },
        {
          type: "match",
          q: "Match the business scenario:",
          matches: [
            { left: "If costs rise,", right: "we'll need to adjust prices" },
            { left: "If the merger succeeds,", right: "we'll become market leaders" },
            { left: "If productivity improves,", right: "we'll meet our targets" }
          ],
          successtip: "✓ BUSINESS SCENARIOS!\nThese conditionals appear in meetings, reports, and presentations! 🎯"
        },
        {
          type: "mc",
          q: "Which shows a professional consequence?",
          options: [
            "If you're late again, you're in trouble",
            "If attendance doesn't improve, we'll need to discuss performance",
            "Come on time or else"
          ],
          a: "If attendance doesn't improve, we'll need to discuss performance",
          tip: "Formal and diplomatic!",
          successtip: "✓ PROFESSIONAL FEEDBACK!\nDiplomatic conditionals soften criticism in the workplace! 🤝"
        },
        {
          type: "traduce",
          q: "Traduce: 'A menos que reduzcamos costos, no alcanzaremos la rentabilidad'",
          words: ["Unless", "we", "reduce", "costs", "we", "won't", "achieve", "profitability"],
          a: "Unless we reduce costs we won't achieve profitability",
          extra: ["don't", "will"],
          successtip: "✓ FINANCIAL WARNING!\nUNLESS adds urgency to financial planning - common in boardrooms! 💰"
        },
        {
          type: "input",
          q: "Complete: 'If the client _____ (approve) the proposal, we _____ (begin) next month.'",
          a: "approves, 'll begin",
          alts: ["approves, will begin"],
          successtip: "✓ CLIENT RELATIONS!\nClient approval conditionals are in every project plan! 📋"
        },
        {
          type: "mc",
          q: "Which is most appropriate for an email?",
          options: [
            "If you want info, tell me",
            "If you require further information, please don't hesitate to contact me",
            "Let me know if you need stuff"
          ],
          a: "If you require further information, please don't hesitate to contact me",
          tip: "Formal closing phrase!",
          successtip: "✓ EMAIL STANDARD!\nThis exact phrase is in MILLIONS of professional emails worldwide! ✉️"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si hay objeciones, las discutiremos en la próxima reunión'",
          words: ["If", "there", "are", "objections", "we'll", "discuss", "them", "in", "the", "next", "meeting"],
          a: "If there are objections we'll discuss them in the next meeting",
          extra: ["will", "is"],
          successtip: "✓ MEETING LANGUAGE!\nThis shows openness to feedback - professional and collaborative! 🗣️"
        },
        {
          type: "match",
          q: "Match the professional email phrase:",
          matches: [
            { left: "If you need assistance,", right: "please let me know" },
            { left: "If this is acceptable,", right: "we can proceed" },
            { left: "If you have questions,", right: "feel free to reach out" }
          ],
          successtip: "✓ EMAIL FORMULAS!\nThese three phrases appear in professional emails CONSTANTLY! 📧"
        }
      ]
    },

    // SET 12
    {
      id: "m9-u9-s12",
      title: "Set 12: Mastery Challenge - Mixed Contexts 🏆",
      context: `**Final Mastery Challenge**

Congratulations! You've learned first conditional in all contexts. Now prove your mastery by handling complex, real-world situations!`,
      
      questions: [
        {
          type: "mc",
          q: "Choose the sentence with the most sophisticated structure:",
          options: [
            "If it rains, I'll stay home",
            "If the economic indicators improve and inflation stabilizes, we'll see growth",
            "I'll go if you go"
          ],
          a: "If the economic indicators improve and inflation stabilizes, we'll see growth",
          tip: "Multiple conditions + technical vocabulary!",
          successtip: "✓ ADVANCED STRUCTURE!\nMultiple conditions + formal vocabulary = C1-level complexity! 🌟"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si inviertes sabiamente y diversificas tu portafolio, minimizarás riesgos'",
          words: ["If", "you", "invest", "wisely", "and", "diversify", "your", "portfolio", "you'll", "minimize", "risks"],
          a: "If you invest wisely and diversify your portfolio you'll minimize risks",
          extra: ["will", "invested"],
          successtip: "✓ FINANCIAL ADVICE!\nThis is sophisticated B2/C1 financial planning language! 💎"
        },
        {
          type: "input",
          q: "Complete with formal vocabulary: 'If we _____ (maintain) quality standards while _____ (optimize) efficiency, we'll remain competitive.'",
          a: "maintain, optimizing",
          alts: ["maintain, optimising"],
          tip: "WHILE can introduce the second condition!",
          successtip: "✓ COMPLEX CONDITIONS!\nWHILE + -ING adds a simultaneous condition - very sophisticated! 🎯"
        },
        {
          type: "match",
          q: "Match the advanced context:",
          matches: [
            { left: "Medical:", right: "If symptoms persist despite treatment, we'll order additional tests" },
            { left: "Legal:", right: "If the contract is breached, we'll pursue legal remedies" },
            { left: "Academic:", right: "If the hypothesis is validated, we'll publish the findings" }
          ],
          successtip: "✓ SPECIALIZED FIELDS!\nFirst conditional is essential in ALL professional fields! 🔬⚖️🎓"
        },
        {
          type: "mc",
          q: "Which best combines IF and UNLESS professionally?",
          options: [
            "If you pay and unless you cancel, we'll ship it",
            "If payment is received and unless cancellation is requested, shipment will proceed",
            "Pay unless you cancel and we ship"
          ],
          a: "If payment is received and unless cancellation is requested, shipment will proceed",
          tip: "Passive voice + formal nouns!",
          successtip: "✓ FORMAL COMBINATION!\nPassive voice + nominalizations (payment, cancellation) = very formal! 📜"
        },
        {
          type: "traduce",
          q: "Traduce: 'A menos que se tomen medidas inmediatas, la situación empeorará'",
          words: ["Unless", "immediate", "action", "is", "taken", "the", "situation", "will", "deteriorate"],
          a: "Unless immediate action is taken the situation will deteriorate",
          extra: ["take", "don't"],
          tip: "Passive voice: 'is taken' not 'they take'!",
          successtip: "✓ CRISIS LANGUAGE!\nPassive + formal vocabulary = language of official warnings! ⚠️"
        },
        {
          type: "input",
          q: "Complete formally: 'If stakeholders _____ (not approve) the budget, the project _____ (be) delayed indefinitely.'",
          a: "don't approve, will be",
          alts: ["do not approve, will be"],
          successtip: "✓ CORPORATE LANGUAGE!\nStakeholder conditionals appear in every major project document! 🏢"
        },
        {
          type: "mc",
          q: "Which shows native-level natural English?",
          options: [
            "If you will need help, I will help you",
            "If you need a hand, just give me a shout",
            "If you are needing assistance, inform me"
          ],
          a: "If you need a hand, just give me a shout",
          tip: "Idioms make it natural!",
          successtip: "✓ NATIVE IDIOMS!\n'Need a hand' and 'give me a shout' = super natural informal English! 🗣️"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si no estás completamente satisfecho, te reembolsaremos sin hacer preguntas'",
          words: ["If", "you're", "not", "completely", "satisfied", "we'll", "refund", "you", "no", "questions", "asked"],
          a: "If you're not completely satisfied we'll refund you no questions asked",
          extra: ["will", "don't"],
          tip: "'No questions asked' = sin hacer preguntas!",
          successtip: "✓ GUARANTEE LANGUAGE!\nThis is THE standard money-back guarantee worldwide! 💯"
        },
        {
          type: "match",
          q: "Match the FINAL MASTERY patterns:",
          matches: [
            { left: "Cause & effect:", right: "If deforestation continues, biodiversity will decline" },
            { left: "Hypothesis:", right: "If the theory is correct, we'll observe the predicted results" },
            { left: "Policy:", right: "If regulations are enforced, compliance will improve" }
          ],
          successtip: "✓ 🎉 MASTERY COMPLETE! 🎉\nYou've mastered first conditional across ALL contexts: casual, professional, technical, and academic! You can now use it naturally for advice, planning, problem-solving, and professional communication. This is essential B2 grammar - you're ready for real-world English! 🌟🏆"
        }
      ]
    }
  ]
};
